// TODO: ManageListsModal + AddSectorModal/AddQualificationModal could be generalized into a
// single parameterised component once there are more list types to manage.

import React, { useState, useEffect } from 'react';
import { Sector, SectorUpdatePayload, Qualification, QualificationUpdatePayload } from '../types';
import { COLORS } from '../../../../core/constants/colors';
import { EditSectorModal } from './EditSectorModal';
import { EditQualificationModal } from './EditQualificationModal';
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog';

type ActiveTab = 'sectors' | 'relationships';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Full list (active + inactive) shown in the manage view. */
  allSectors: Sector[];
  onUpdateSector: (id: number, payload: SectorUpdatePayload) => Promise<Sector | null>;
  onDeleteSector: (id: number) => Promise<void>;
  /** Full list (active + inactive) shown in the manage view. */
  allQualifications: Qualification[];
  onUpdateQualification: (id: number, payload: QualificationUpdatePayload) => Promise<Qualification | null>;
  onDeleteQualification: (id: number) => Promise<void>;
}

export const ManageListsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  allSectors,
  onUpdateSector,
  onDeleteSector,
  allQualifications,
  onUpdateQualification,
  onDeleteQualification,
}) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('sectors');

  // Edit state
  const [editSector, setEditSector] = useState<Sector | null>(null);
  const [editQualification, setEditQualification] = useState<Qualification | null>(null);

  // Delete state
  const [deleteSectorTarget, setDeleteSectorTarget] = useState<Sector | null>(null);
  const [deleteQualTarget, setDeleteQualTarget] = useState<Qualification | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);


  // Reset sub-modal state whenever the modal closes so it always opens fresh.
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('sectors');
      setEditSector(null);
      setEditQualification(null);
      setDeleteSectorTarget(null);
      setDeleteQualTarget(null);
      setIsDeleting(false);
      setDeleteError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDeleteSectorConfirm = async () => {
    if (!deleteSectorTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await onDeleteSector(deleteSectorTarget.id);
      setDeleteSectorTarget(null);
    } catch (err: any) {
      setDeleteError(err?.message || 'Failed to delete sector. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteQualConfirm = async () => {
    if (!deleteQualTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await onDeleteQualification(deleteQualTarget.id);
      setDeleteQualTarget(null);
    } catch (err: any) {
      setDeleteError(err?.message || 'Failed to delete relationship. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity"
        onClick={onClose}
      />
      <div className="fixed inset-0 flex items-center justify-center z-[60] p-4">
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <h3 className="text-lg font-bold" style={{ color: '#1F2937' }}>Manage Lists</h3>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-all"
              style={{ color: '#6B7280' }}
              type="button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <button
              onClick={() => setActiveTab('sectors')}
              className={`flex-1 py-3 text-sm font-medium transition-all ${activeTab === 'sectors' ? 'border-b-2' : ''}`}
              style={{
                color: activeTab === 'sectors' ? COLORS.admin.primary : '#6B7280',
                borderColor: activeTab === 'sectors' ? COLORS.admin.primary : 'transparent',
              }}
            >
              Sectors ({allSectors.length})
            </button>
            <button
              onClick={() => setActiveTab('relationships')}
              className={`flex-1 py-3 text-sm font-medium transition-all ${activeTab === 'relationships' ? 'border-b-2' : ''}`}
              style={{
                color: activeTab === 'relationships' ? COLORS.admin.primary : '#6B7280',
                borderColor: activeTab === 'relationships' ? COLORS.admin.primary : 'transparent',
              }}
            >
              Relationships ({allQualifications.length})
            </button>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'sectors' && (
              <ul>
                {allSectors.length === 0 && (
                  <li className="px-6 py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>
                    No sectors yet. Use "Add Sector" to create one.
                  </li>
                )}
                {allSectors.map((sector) => (
                  <li
                    key={sector.id}
                    className="flex items-center gap-3 px-6 py-4 border-b"
                    style={{ borderColor: '#E5E7EB' }}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: '#1F2937' }}>{sector.name}</p>
                      <p className="text-xs truncate" style={{ color: '#6B7280' }}>{sector.slug}</p>
                    </div>
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
                      style={{
                        backgroundColor: sector.is_active ? '#D1FAE5' : '#F3F4F6',
                        color: sector.is_active ? '#059669' : '#6B7280',
                      }}
                    >
                      {sector.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      onClick={() => setEditSector(sector)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 transition-all flex-shrink-0"
                      title="Edit"
                      style={{ color: '#6B7280' }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => { setDeleteError(null); setDeleteSectorTarget(sector); }}
                      className="p-1.5 rounded-lg hover:bg-red-50 transition-all flex-shrink-0"
                      title="Delete"
                      style={{ color: '#DC2626' }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {activeTab === 'relationships' && (
              <ul>
                {allQualifications.length === 0 && (
                  <li className="px-6 py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>
                    No relationships yet. Use "Add Relationship" to create one.
                  </li>
                )}
                {allQualifications.map((qual) => (
                  <li
                    key={qual.id}
                    className="flex items-center gap-3 px-6 py-4 border-b"
                    style={{ borderColor: '#E5E7EB' }}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: '#1F2937' }}>{qual.name}</p>
                      <p className="text-xs truncate" style={{ color: '#6B7280' }}>{qual.slug}</p>
                    </div>
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
                      style={{
                        backgroundColor: qual.is_active ? '#D1FAE5' : '#F3F4F6',
                        color: qual.is_active ? '#059669' : '#6B7280',
                      }}
                    >
                      {qual.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      onClick={() => setEditQualification(qual)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 transition-all flex-shrink-0"
                      title="Edit"
                      style={{ color: '#6B7280' }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => { setDeleteError(null); setDeleteQualTarget(qual); }}
                      className="p-1.5 rounded-lg hover:bg-red-50 transition-all flex-shrink-0"
                      title="Delete"
                      style={{ color: '#DC2626' }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <button
              onClick={onClose}
              className="w-full px-4 py-2.5 rounded-lg font-medium transition-all hover:bg-gray-100"
              style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Edit Sector modal — z-70 so it sits above this modal */}
      <EditSectorModal
        isOpen={!!editSector}
        sector={editSector}
        onClose={() => setEditSector(null)}
        onSubmit={async (id, payload) => {
          await onUpdateSector(id, payload);
          setEditSector(null);
        }}
      />

      {/* Edit Qualification modal */}
      <EditQualificationModal
        isOpen={!!editQualification}
        qualification={editQualification}
        onClose={() => setEditQualification(null)}
        onSubmit={async (id, payload) => {
          await onUpdateQualification(id, payload);
          setEditQualification(null);
        }}
      />

      {/* Delete Sector confirmation */}
      <DeleteConfirmationDialog
        isOpen={!!deleteSectorTarget}
        companyName={deleteSectorTarget?.name ?? ''}
        title="Delete Sector"
        itemLabel="delete the sector"
        onConfirm={handleDeleteSectorConfirm}
        onCancel={() => { setDeleteSectorTarget(null); setDeleteError(null); }}
        isDeleting={isDeleting}
        errorMessage={deleteError ?? undefined}
      />

      {/* Delete Qualification confirmation */}
      <DeleteConfirmationDialog
        isOpen={!!deleteQualTarget}
        companyName={deleteQualTarget?.name ?? ''}
        title="Delete Relationship"
        itemLabel="delete the relationship"
        onConfirm={handleDeleteQualConfirm}
        onCancel={() => { setDeleteQualTarget(null); setDeleteError(null); }}
        isDeleting={isDeleting}
        errorMessage={deleteError ?? undefined}
      />
    </>
  );
};
